import { gql, useQuery } from '@apollo/client';
import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import { Button, CardBody, CardHeader, CardTitle } from '@apollo/orbit';
import _ from 'lodash';
import React from 'react';

import { useInternalGraphLinking } from 'src/app/graph/hooks/useInternalGraphLinking';
import { Loading } from 'src/components/common/loading/Loading';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import {
  ProposalCheckRerunButtonWorkflowFragment,
  ProposalsCheckRerunButton,
} from '../../proposalChecksRerunButton/ProposalChecksRerunButton';

import { BuildDetailsErrorItem } from './buildDetailsErrorItem/BuildDetailsErrorItem';
import { EmptyBuildErrors } from './emptyBuildErrors/EmptyBuildErrors';

export function BuildCheckDetails() {
  const { currentBuildCheckDetailsRoute } = useInternalGraphLinking();
  const { checkId, graphId, graphVariant } = useRouteParams(
    currentBuildCheckDetailsRoute,
  );
  const { data, loading, error } = useQuery<
    GraphQLTypes.BuildCheckDetailsQuery,
    GraphQLTypes.BuildCheckDetailsQueryVariables
  >(
    gql`
      query BuildCheckDetailsQuery($graphId: ID!, $checkId: ID!) {
        service(id: $graphId) {
          id
          checkWorkflow(id: $checkId) {
            id
            buildTask {
              id
              ... on CompositionCheckTask {
                result {
                  graphCompositionID
                  errors {
                    code
                    message
                    locations {
                      line
                      column
                    }
                  }
                  supergraphSdl
                }
              }
            }
            ...ProposalCheckRerunButtonWorkflowFragment
          }
        }
      }
      ${ProposalCheckRerunButtonWorkflowFragment}
    `,
    {
      variables: { graphId, checkId },
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
    },
  );
  const workflow = data?.service?.checkWorkflow;
  const task = workflow?.buildTask;

  if (loading) {
    return <Loading />;
  } else if (error) {
    return (
      <div className="p-2">
        <ErrorMessage error={error} />
      </div>
    );
  } else if (task?.__typename !== 'CompositionCheckTask') {
    return <NotFound />;
  }

  const results = task.result;

  if (!isMinLengthArray(1, results?.errors)) {
    return (
      <EmptyBuildErrors
        supergraphSdl={results?.supergraphSdl}
        graphId={graphId}
      />
    );
  }

  return (
    <>
      <CardHeader>
        <CardTitle>Build errors</CardTitle>
        {workflow?.proposalRevision?.id && graphVariant && (
          <ProposalsCheckRerunButton
            graphId={graphId}
            graphVariant={graphVariant}
            proposalRevisionId={workflow.proposalRevision.id}
            checkWorkflowId={workflow.id}
            trigger={
              <Button
                size="md"
                variant="secondary"
                rightIcon={<IconChevronDown />}
              >
                Rerun check
              </Button>
            }
          />
        )}
      </CardHeader>
      <CardBody className="overflow-y-auto p-0 last:pb-0">
        {results?.errors.map((buildError, i) => (
          <BuildDetailsErrorItem
            // eslint-disable-next-line react/no-array-index-key
            key={`error-${i}`}
            error={{
              message: buildError.message,
              code: buildError.code,
            }}
          />
        ))}
      </CardBody>
    </>
  );
}
